import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Hook for managing task selection in Gantt view
 * 
 * Features:
 * - Ctrl+A to select all tasks
 * - DEL/Backspace to delete selected tasks
 * - Right-click + drag to draw selection box
 * - Click on bar to select single task
 * - Click on empty area to deselect all
 */
export const useGanttSelection = (chartRef, tasks, dateRange, onDeleteTasks) => {
  // Set of selected task IDs
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  
  // Sync DOM with React state when state changes (for external updates like selection box, selectAll)
  // This ensures the DOM reflects the selection state when it changes via setState
  useEffect(() => {
    // Clear all selected classes first
    document.querySelectorAll('.gantt-bar--selected').forEach(el => {
      el.classList.remove('gantt-bar--selected');
    });
    // Add selected class to currently selected bars
    selectedTaskIds.forEach(taskId => {
      const barEl = document.querySelector(`.gantt-bar[data-task-id="${taskId}"]`);
      barEl?.classList.add('gantt-bar--selected');
    });
  }, [selectedTaskIds]);
  
  // Selection box state
  const [selectionBox, setSelectionBox] = useState(null);
  const selectionStartRef = useRef(null);
  const isSelectingRef = useRef(false);

  // Select all tasks (Ctrl+A)
  const selectAll = useCallback(() => {
    const allIds = new Set(tasks.map(t => t.id));
    setSelectedTaskIds(allIds);
  }, [tasks]);

  // Clear selection
  // PERFORMANCE: Instantly update DOM for visual feedback
  const clearSelection = useCallback(() => {
    document.querySelectorAll('.gantt-bar--selected').forEach(el => {
      el.classList.remove('gantt-bar--selected');
    });
    setSelectedTaskIds(new Set());
  }, []);

  // Toggle single task selection
  // PERFORMANCE: Instantly update DOM for visual feedback BEFORE React state update
  const toggleTaskSelection = useCallback((taskId, addToSelection = false) => {
    const barEl = document.querySelector(`.gantt-bar[data-task-id="${taskId}"]`);
    const wasSelected = barEl?.classList.contains('gantt-bar--selected');
    
    // If not adding to selection, clear all others first
    if (!addToSelection) {
      document.querySelectorAll('.gantt-bar--selected').forEach(el => {
        el.classList.remove('gantt-bar--selected');
      });
    }
    
    // Toggle this bar
    if (wasSelected && addToSelection) {
      barEl?.classList.remove('gantt-bar--selected');
    } else {
      barEl?.classList.add('gantt-bar--selected');
    }
    
    // Update React state
    setSelectedTaskIds(prev => {
      const newSet = addToSelection ? new Set(prev) : new Set();
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  }, []);

  // Select single task (replacing selection)
  // PERFORMANCE: Instantly update DOM for visual feedback
  const selectTask = useCallback((taskId) => {
    // Clear all selections first
    document.querySelectorAll('.gantt-bar--selected').forEach(el => {
      el.classList.remove('gantt-bar--selected');
    });
    // Add selection to new bar
    const barEl = document.querySelector(`.gantt-bar[data-task-id="${taskId}"]`);
    barEl?.classList.add('gantt-bar--selected');
    setSelectedTaskIds(new Set([taskId]));
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }
      
      // Ctrl+A - select all
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        // Only if Gantt view is focused (chart exists and is visible)
        if (chartRef.current && chartRef.current.closest('.gantt-view')) {
          e.preventDefault();
          selectAll();
        }
      }
      
      // DEL or Backspace - delete selected tasks
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedTaskIds.size > 0) {
        // Only if Gantt view is focused
        if (chartRef.current && chartRef.current.closest('.gantt-view')) {
          e.preventDefault();
          if (onDeleteTasks) {
            const idsToDelete = Array.from(selectedTaskIds);
            onDeleteTasks(idsToDelete);
            // Clear selection after delete
            clearSelection();
          }
        }
      }
      
      // Escape - clear selection
      if (e.key === 'Escape') {
        clearSelection();
        setSelectionBox(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [chartRef, selectAll, clearSelection, selectedTaskIds, onDeleteTasks]);

  // Listen for focusMission event from search/nesting
  useEffect(() => {
    const handleFocusMission = (e) => {
      const { missionId, view, triggerEdit } = e.detail;
      if (view !== 'gantt' || !missionId) return;
      
      // Find the row element and scroll it into view
      setTimeout(() => {
        // Try to find the row first (for scrolling to sidebar)
        const rowElement = document.querySelector(`.gantt-row[data-gantt-task-id="${missionId}"]`);
        const barElement = document.querySelector(`.gantt-bar[data-task-id="${missionId}"]`);
        
        // Scroll the row into view (this will show the sidebar with the name)
        if (rowElement) {
          // Scroll row vertically into center
          rowElement.scrollIntoView({ behavior: 'instant', block: 'center' });
          
          // Also scroll bar horizontally into center if it exists
          if (barElement) {
            barElement.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'center' });
          }
          
          // Check if task has placeholder name (newly created)
          const nameElement = rowElement.querySelector('.gantt-row__name');
          const taskName = nameElement?.textContent || '';
          const isPlaceholder = !taskName || 
            taskName === 'Untitled mission' || 
            taskName === 'Untitled decision' ||
            taskName === 'ENTER MISSION NAME' ||
            taskName === 'New Decision';
          
          // Trigger editing for new tasks or if explicitly requested
          if (triggerEdit || isPlaceholder) {
            window.dispatchEvent(new CustomEvent('ganttStartEditing', {
              detail: { taskId: missionId }
            }));
          }
          
          // Flash highlight the row and bar immediately after instant scroll
          rowElement.classList.add('gantt-row--highlighted');
          if (barElement) {
            barElement.classList.add('gantt-bar--highlighted');
          }
          setTimeout(() => {
            rowElement.classList.remove('gantt-row--highlighted');
            if (barElement) {
              barElement.classList.remove('gantt-bar--highlighted');
            }
          }, 1000);
          
          // Select the task
          selectTask(missionId);
        } else if (barElement) {
          // Fallback to bar element if row not found
          barElement.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'center' });
          
          // Flash highlight immediately
          barElement.classList.add('gantt-bar--highlighted');
          setTimeout(() => {
            barElement.classList.remove('gantt-bar--highlighted');
          }, 1000);
          
          selectTask(missionId);
        }
      }, 100);
    };
    
    window.addEventListener('focusMission', handleFocusMission);
    return () => window.removeEventListener('focusMission', handleFocusMission);
  }, [selectTask]);

  // Handle right-click drag for selection box
  const handleSelectionMouseDown = useCallback((e) => {
    // Only right mouse button
    if (e.button !== 2) return;
    
    // Don't start selection if clicking on a bar
    if (e.target.closest('.gantt-bar')) return;
    
    // Don't start selection if clicking on sidebar
    if (e.target.closest('.gantt-row__sidebar')) return;
    if (e.target.closest('.gantt-timeline-header__sidebar')) return;
    if (e.target.closest('.gantt-swimlane__header')) return;
    
    const chart = chartRef.current;
    if (!chart) return;
    
    const rect = chart.getBoundingClientRect();
    const startX = e.clientX - rect.left + chart.scrollLeft;
    const startY = e.clientY - rect.top + chart.scrollTop;
    
    selectionStartRef.current = { x: startX, y: startY, clientX: e.clientX, clientY: e.clientY };
    isSelectingRef.current = true;
    
    // Prevent context menu
    e.preventDefault();
  }, [chartRef]);

  // Handle mouse move during selection
  useEffect(() => {
    if (!chartRef.current) return;

    const handleMouseMove = (e) => {
      if (!isSelectingRef.current || !selectionStartRef.current) return;
      
      const chart = chartRef.current;
      const rect = chart.getBoundingClientRect();
      const currentX = e.clientX - rect.left + chart.scrollLeft;
      const currentY = e.clientY - rect.top + chart.scrollTop;
      
      const start = selectionStartRef.current;
      
      setSelectionBox({
        left: Math.min(start.x, currentX),
        top: Math.min(start.y, currentY),
        width: Math.abs(currentX - start.x),
        height: Math.abs(currentY - start.y),
      });
    };

    const handleMouseUp = (e) => {
      if (!isSelectingRef.current) return;
      
      // If we have a selection box, find intersecting bars
      if (selectionBox && chartRef.current) {
        const bars = chartRef.current.querySelectorAll('.gantt-bar');
        const newSelection = new Set();
        
        bars.forEach(bar => {
          const barRect = bar.getBoundingClientRect();
          const chartRect = chartRef.current.getBoundingClientRect();
          
          // Convert bar position to chart-relative coordinates
          const barLeft = barRect.left - chartRect.left + chartRef.current.scrollLeft;
          const barTop = barRect.top - chartRect.top + chartRef.current.scrollTop;
          const barRight = barLeft + barRect.width;
          const barBottom = barTop + barRect.height;
          
          // Check intersection with selection box
          const boxRight = selectionBox.left + selectionBox.width;
          const boxBottom = selectionBox.top + selectionBox.height;
          
          const intersects = !(
            barRight < selectionBox.left ||
            barLeft > boxRight ||
            barBottom < selectionBox.top ||
            barTop > boxBottom
          );
          
          if (intersects) {
            // Get task ID from closest row
            const row = bar.closest('.gantt-row');
            if (row) {
              const taskId = bar.getAttribute('data-task-id');
              if (taskId) {
                newSelection.add(taskId);
              }
            }
          }
        });
        
        // If Ctrl/Cmd held, add to existing selection
        if (e.ctrlKey || e.metaKey) {
          setSelectedTaskIds(prev => {
            const combined = new Set(prev);
            newSelection.forEach(id => combined.add(id));
            return combined;
          });
        } else {
          setSelectedTaskIds(newSelection);
        }
      }
      
      isSelectingRef.current = false;
      selectionStartRef.current = null;
      setSelectionBox(null);
    };

    const handleContextMenu = (e) => {
      // Prevent context menu while selecting
      if (isSelectingRef.current) {
        e.preventDefault();
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [chartRef, selectionBox]);

  return {
    selectedTaskIds,
    selectionBox,
    selectAll,
    clearSelection,
    toggleTaskSelection,
    selectTask,
    handleSelectionMouseDown,
    isSelecting: isSelectingRef.current,
  };
};

export default useGanttSelection;
